import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Heart, User, Menu, X, Phone, MapPin, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useState, useEffect } from 'react';
import { AuthModal } from './AuthModal';
import { useSiteSettings } from '../hooks/useSiteSettings';

export function Header() {
  const { user } = useAuth();
  const { items } = useCart();
  const location = useLocation();
  const { settings } = useSiteSettings();
  const [showAuth, setShowAuth] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Количество товаров в корзине
  const cartItemsCount = items.reduce((sum, item) => sum + item.quantity, 0);

  // Отслеживание скролла
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Закрытие меню при изменении маршрута
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { href: '/catalog', label: 'Каталог', highlight: true },
    { href: '/about', label: 'О нас' },
    { href: '/service', label: 'Сервис' },
    { href: '/delivery', label: 'Доставка' },
    { href: '/marketplaces', label: 'Маркетплейсы' },
    { href: '/requisites', label: 'Реквизиты' },
  ];

  return (
    <>
      {/* Верхняя полоса с контактами */}
      <div className="bg-gray-900 text-white py-2 hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <a href={`tel:${settings.phone_number.replace(/[^\d+]/g, '')}`} className="flex items-center gap-2 hover:text-[#FFCC00] transition-colors">
                <Phone className="w-4 h-4" />
                <span>{settings.phone_number}</span>
              </a>
              <span className="flex items-center gap-2 text-gray-400">
                <MapPin className="w-4 h-4" />
                <span>{settings.address}</span>
              </span>
            </div>
            <div className="flex items-center gap-4 text-gray-400">
              <span>{settings.working_hours}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Основной хедер */}
      <header 
        className={`bg-[#FFCC00] sticky top-0 z-40 transition-shadow duration-300 ${
          isScrolled ? 'shadow-lg' : 'shadow-md'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-18">
            {/* Логотип */}
            <Link to="/" className="flex items-center gap-2 md:gap-3 group">
              <div className="w-9 h-9 md:w-10 md:h-10 bg-black rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                <img src="/logo.svg" alt="Big PineApple" className="w-6 h-6 md:w-7 md:h-7" />
              </div>
              <div className="hidden sm:block">
                <span className="text-xl md:text-2xl font-bold text-black">Big PineApple</span>
                <span className="hidden lg:block text-xs text-black/60 -mt-1">Ноутбуки из США</span>
              </div>
            </Link>

            {/* Десктопная навигация */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    link.highlight 
                      ? 'bg-black text-[#FFCC00] hover:bg-gray-900' 
                      : location.pathname === link.href
                        ? 'bg-black/10 text-black'
                        : 'text-black/80 hover:bg-black/5 hover:text-black'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Иконки действий */}
            <div className="flex items-center gap-2 md:gap-3">
              {/* Телефон на мобильных */}
              <a 
                href={`tel:${settings.phone_number.replace(/[^\d+]/g, '')}`}
                className="md:hidden p-2 hover:bg-black/10 rounded-lg transition-colors"
              >
                <Phone className="w-5 h-5" />
              </a>

              {/* Избранное */}
              <Link 
                to="/favorites" 
                className="p-2 hover:bg-black/10 rounded-lg transition-colors relative"
              >
                <Heart className="w-5 h-5 md:w-6 md:h-6" />
              </Link>

              {/* Корзина */}
              <Link 
                to="/cart" 
                className="p-2 hover:bg-black/10 rounded-lg transition-colors relative"
              >
                <ShoppingCart className="w-5 h-5 md:w-6 md:h-6" />
                {cartItemsCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-black text-[#FFCC00] text-xs font-bold rounded-full flex items-center justify-center">
                    {cartItemsCount > 9 ? '9+' : cartItemsCount}
                  </span>
                )}
              </Link>

              {/* Аккаунт */}
              {user ? (
                <Link 
                  to="/account" 
                  className="p-2 hover:bg-black/10 rounded-lg transition-colors"
                >
                  <div className="w-5 h-5 md:w-6 md:h-6 bg-black text-[#FFCC00] rounded-full flex items-center justify-center text-xs font-bold">
                    {user.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                </Link>
              ) : (
                <button 
                  onClick={() => setShowAuth(true)} 
                  className="p-2 hover:bg-black/10 rounded-lg transition-colors"
                >
                  <User className="w-5 h-5 md:w-6 md:h-6" />
                </button>
              )}

              {/* Мобильное меню */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 hover:bg-black/10 rounded-lg transition-colors"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Мобильное меню */}
        <div 
          className={`md:hidden absolute top-full left-0 right-0 bg-[#FFCC00] shadow-lg overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="container mx-auto px-4 py-4">
            <div className="space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`block px-4 py-3 rounded-xl font-medium transition-colors ${
                    link.highlight 
                      ? 'bg-black text-[#FFCC00]'
                      : 'hover:bg-black/10'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            
            {/* Контакты в мобильном меню */}
            <div className="mt-4 pt-4 border-t border-black/10">
              <a 
                href={`tel:${settings.phone_number.replace(/[^\d+]/g, '')}`}
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-black/10 transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span className="font-medium">{settings.phone_number}</span>
              </a>
              <div className="flex items-center gap-3 px-4 py-3 text-black/70">
                <MapPin className="w-5 h-5" />
                <span className="text-sm">{settings.address}</span>
              </div>
              <div className="px-4 py-2 text-sm text-black/60">
                {settings.working_hours}
              </div>
            </div>
          </div>
        </div>
      </header>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </>
  );
}
